import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  isDataExportAdmin,
  isDataExportAllowlistConfigured,
  isDataExportExcludeListConfigured,
  getDataExportExcludeLists,
} from '../../config/dataExportAdmins';
import { fetchFeedbackExportPayload, DEFAULT_EXPORT_FROM_YMD } from '../../services/feedbackExportData';
import { downloadFeedbackExportXlsx } from '../../utils/feedbackExportExcel';
import { FIREBASE_WEB_APP_ENV_NAMES, getFirebaseWebEnvDiagnostics } from '../../firebase';
import './AdminDataExport.css';

export default function AdminDataExport() {
  const { firebaseUser, useFirebase } = useAuth();
  const [exportFromYmd, setExportFromYmd] = useState(DEFAULT_EXPORT_FROM_YMD);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const allowed = isDataExportAdmin(firebaseUser?.email, firebaseUser?.uid);
  const allowlistConfigured = isDataExportAllowlistConfigured();
  const excludeListConfigured = isDataExportExcludeListConfigured();
  const excludeLists = getDataExportExcludeLists();
  const firebaseEnvDiag = getFirebaseWebEnvDiagnostics();

  const handleDownload = useCallback(async () => {
    setError('');
    setStatus('loading');
    try {
      const payload = await fetchFeedbackExportPayload({ exportFromYmd });
      const safeYmd = String(exportFromYmd || DEFAULT_EXPORT_FROM_YMD).replace(/[^0-9-]/g, '') || DEFAULT_EXPORT_FROM_YMD;
      downloadFeedbackExportXlsx(payload, `lotus-lms-feedback-from-${safeYmd}`);
      setStatus('done');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (e) {
      const msg = e?.message || String(e);
      setError(msg);
      setStatus('error');
    }
  }, [exportFromYmd]);

  if (!allowed) {
    const signedIn = Boolean(firebaseUser?.uid);
    const who = firebaseUser?.email || firebaseUser?.uid || '(unknown account)';

    return (
      <div className="admin-export-page">
        <div className="admin-export-inner">
          <nav className="admin-export-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true"> / </span>
            <span>Data export</span>
          </nav>

          <header className="admin-export-hero">
            <h1 className="admin-export-title">Data export - access restricted</h1>
            <p className="admin-export-lead">
              This page is only available to accounts listed in the admin allowlist.
            </p>
          </header>

          <div className="admin-export-banner admin-export-banner--warn" role="status">
            {signedIn ? (
              <>
                <p>
                  You are signed in as <strong>{who}</strong>, but this account is not on the export allowlist.
                </p>
                <p className="admin-export-hint" style={{ marginTop: '0.75rem' }}>
                  Add this exact email (or your Firebase user UID) to <code>.env</code>, then stop and restart{' '}
                  <code>npm start</code> so Create React App reloads env vars:
                </p>
                <pre className="admin-export-codeblock">
                  {`REACT_APP_DATA_EXPORT_ADMIN_EMAILS=${firebaseUser?.email || 'your-email@example.com'}`}
                </pre>
                <p className="admin-export-hint">
                  Optional UID allowlist (comma-separated): <code>REACT_APP_DATA_EXPORT_ADMIN_UIDS</code>
                </p>
              </>
            ) : (
              <p>You are not signed in with Firebase. Sign in with an admin account, or use demo mode (export is not available without Firebase).</p>
            )}

            {!allowlistConfigured && useFirebase && (
              <p className="admin-export-hint" style={{ marginTop: '0.75rem' }}>
                <strong>Note:</strong> No allowlist is configured in <code>.env</code> yet - nobody can open the export
                screen until <code>REACT_APP_DATA_EXPORT_ADMIN_EMAILS</code> or <code>REACT_APP_DATA_EXPORT_ADMIN_UIDS</code>{' '}
                is set.
              </p>
            )}
          </div>

          <p>
            <Link to="/" className="admin-export-breadcrumb">
              ← Back to home
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-export-page">
      <div className="admin-export-inner">
        <nav className="admin-export-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true"> / </span>
          <span>Data export</span>
        </nav>

        <header className="admin-export-hero">
          <h1 className="admin-export-title">Pilot data export</h1>
          <p className="admin-export-lead">
            Download an Excel workbook with five sheets: <strong>Registered users</strong> (everyone with a Firestore{' '}
            <code>users</code> profile after sign-in), then research consent supplements (enrollment), website feedback
            from the floating button, end-of-module workplace surveys, and optional participant reviews. Each submission row
            includes <strong>Participant email address</strong> and <strong>Participant display name</strong> (left
            columns), then the stored submission fields.
            Email on feedback rows can come from the form; otherwise email and display name are read from{' '}
            <code>users/&lt;uid&gt;</code> after each user signs in (sync for export). Older rows may show blank email
            until that participant logs in again after this app update.
          </p>
        </header>

        {!useFirebase && (
          <div className="admin-export-banner admin-export-banner--warn" role="alert">
            <p>
              <strong>Firebase is not active in this build.</strong> Data export reads Firestore, so the web app
              needs your Firebase project keys in a root <code>.env</code> file (same folder as <code>package.json</code>
              ), then a full dev-server restart.
            </p>
            <ul className="admin-export-checklist">
              <li>
                In{' '}
                <a href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer">
                  Firebase Console
                </a>
                : Project settings → General → Your apps → Web app → copy the config values.
              </li>
              <li>
                Add every line below to <code>.env</code> (see <code>.env.example</code> and <code>AUTH_SETUP.md</code>
                ). The export admin line (<code>REACT_APP_DATA_EXPORT_ADMIN_EMAILS</code>) does <em>not</em> turn on
                Firebase by itself.
              </li>
              <li>
                Stop the dev server (<kbd>Ctrl</kbd>+<kbd>C</kbd>) and run <code>npm start</code> again so Create React
                App picks up new variables.
              </li>
              <li>Sign in with a real Firebase account (not demo mode), then return here.</li>
            </ul>
            {!firebaseEnvDiag.apiKeySet ? (
              <p className="admin-export-hint">
                Right now <code>REACT_APP_FIREBASE_API_KEY</code> is missing or blank - that is what enables Firebase in
                this app.
              </p>
            ) : firebaseEnvDiag.missingNames.length > 0 ? (
              <p className="admin-export-hint">
                <code>REACT_APP_FIREBASE_API_KEY</code> is set, but these are still empty in this build:{' '}
                <code>{firebaseEnvDiag.missingNames.join(', ')}</code>. Copy them from the Firebase web app config,
                then restart <code>npm start</code>.
              </p>
            ) : (
              <p className="admin-export-hint">
                All Firebase web keys appear in this build. If you still see this, stop the dev server and run{' '}
                <code>npm start</code> again, then hard-refresh the browser. Check the browser console for Firebase
                errors.
              </p>
            )}
            <pre className="admin-export-codeblock" aria-label="Example env variable names">
              {FIREBASE_WEB_APP_ENV_NAMES.join('\n')}
            </pre>
          </div>
        )}

        {error && (
          <div className="admin-export-banner admin-export-banner--error" role="alert">
            {error}
            <p className="admin-export-hint">
              If you see a permission error, the signed-in user must be allowed to <strong>list</strong> these
              collections in Firestore rules (the app uses <code>getDocs</code>, not only single-document reads).
            </p>
            {/permission|insufficient/i.test(String(error)) && (
              <div className="admin-export-hint" style={{ marginTop: '0.75rem' }}>
                <p>
                  Confirm Firestore rules allow your admin account to <strong>query</strong> these collections with a{' '}
                  <code>{`where('createdAt', '>=', …)`}</code> filter. If Firebase asks for a composite index, open the
                  link in the error and create it.
                </p>
                <ul className="admin-export-checklist">
                  <li>
                    <strong>Firebase Console:</strong> Firestore Database → <strong>Rules</strong> → paste the rules
                    (or merge the export-related <code>match</code> blocks) → <strong>Publish</strong>.
                  </li>
                  <li>
                    <strong>CLI:</strong> from the project root,{' '}
                    <code>firebase deploy --only firestore:rules</code> (requires <code>firebase login</code> and{' '}
                    <code>firebase use &lt;your-project&gt;</code>).
                  </li>
                </ul>
                <p className="admin-export-hint">
                  See <code>FEEDBACK_SETUP.md</code> → section <em>Pilot data export (Excel)</em> for the full pattern.
                </p>
              </div>
            )}
          </div>
        )}

        <section className="admin-export-card" aria-labelledby="export-action-heading">
          <h2 id="export-action-heading" className="admin-export-card-title">
            Download workbook
          </h2>
          <p className="admin-export-card-text">
            Sheets: <strong>Registered users</strong> (all LMS profiles; not filtered by date), then{' '}
            <strong>Research consent</strong>, <strong>Website feedback</strong>, <strong>Workplace surveys</strong>,{' '}
            <strong>Participant reviews</strong>. Column headers use full descriptive names. The four submission sheets
            only include rows with <code>createdAt</code> on or after the date below (local timezone). The user list
            includes everyone in Firebase Authentication when the <code>listUsersForExport</code> Cloud Function is
            deployed (see <code>functions/</code> and <code>FEEDBACK_SETUP.md</code>). Without it, only Firestore{' '}
            <code>users</code> profiles and submission <code>userId</code>s are listed.
            {excludeListConfigured && (
              <>
                {' '}
                Rows for accounts listed in <code>REACT_APP_DATA_EXPORT_EXCLUDE_EMAILS</code> or{' '}
                <code>REACT_APP_DATA_EXPORT_EXCLUDE_UIDS</code> are omitted (
                {excludeLists.emails.length} email{excludeLists.emails.length === 1 ? '' : 's'},{' '}
                {excludeLists.uids.length} UID{excludeLists.uids.length === 1 ? '' : 's'}).
              </>
            )}
          </p>
          <div className="admin-export-date-row">
            <label htmlFor="export-from-date" className="admin-export-date-label">
              Include records from (inclusive)
            </label>
            <input
              id="export-from-date"
              type="date"
              className="admin-export-date-input"
              value={exportFromYmd}
              onChange={(e) => setExportFromYmd(e.target.value || DEFAULT_EXPORT_FROM_YMD)}
              disabled={!useFirebase || status === 'loading'}
            />
            <p className="admin-export-date-hint">
              Default <strong>{DEFAULT_EXPORT_FROM_YMD}</strong> means “from 7 May 2026 onward” (i.e. after 6 May).
              Change this to widen or narrow the export.
            </p>
          </div>
          <button
            type="button"
            className="admin-export-btn"
            onClick={handleDownload}
            disabled={!useFirebase || status === 'loading'}
          >
            {status === 'loading' ? 'Preparing file…' : 'Export to Excel (.xlsx)'}
          </button>
          {status === 'done' && <p className="admin-export-success">Download started.</p>}
        </section>
      </div>
    </div>
  );
}
